import { useState, type ReactNode } from "react";
import { cardTitleTextClass, sectionCardClass } from "@/components/common/surfaceClasses";

/**
 * Canonical mode-panel sub-section primitive (style doctrine \u00a7K2).
 *
 * Default chrome: `rounded-2xl border border-gray-200 bg-white p-4 space-y-3 shadow-sm`
 * + dark variants. Every mode-panel sub-section should reach for this rather
 * than inlining its own `rounded-2xl border ...` recipe; see
 * `docs/planning/container_padding_pass.md` for the rules.
 *
 * The legacy import path `@/components/kb-admin/SectionCard` re-exports from
 * here for back-compat; new code should import from `@/components/common/SectionCard`.
 */
export function SectionCard(props: {
  /** String titles render as the canonical `<h3>`; pass a ReactNode for custom chrome (e.g. segmented toggles). */
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  rightSlot?: ReactNode;
  /**
   * Optional extra classes to merge onto the card's `<section>` element.
   * Reach for this when the card needs to participate in a layout the
   * parent owns -- e.g. `h-full` for grid-row stretch alignment, or a
   * `md:col-span-2` directly on the card without a wrapper div. The
   * default chrome (rounded-2xl border / shadow / padding / dark
   * variants) is preserved and your classes append to it.
   */
  className?: string;
  /** Header toggles the body. Starts closed when `defaultCollapsed` is set. */
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  /** Controlled open state. Omit to let the card remember its own toggle. */
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
}) {
  const collapsible = props.collapsible || props.defaultCollapsed === true || props.collapsed !== undefined;
  const [uncontrolledCollapsed, setUncontrolledCollapsed] = useState(Boolean(props.defaultCollapsed));
  const collapsed = props.collapsed ?? uncontrolledCollapsed;
  function toggleCollapsed() {
    const next = !collapsed;
    props.onCollapsedChange?.(next);
    if (props.collapsed === undefined) setUncontrolledCollapsed(next);
  }
  const sectionClass = `${sectionCardClass} min-w-0${collapsible && collapsed ? " overflow-hidden" : ""}${props.className ? ` ${props.className}` : ""}`;
  const title = typeof props.title === "string" ? (
    <h3 className={`min-w-0 break-words text-sm font-semibold ${cardTitleTextClass}`}>{props.title}</h3>
  ) : (
    props.title
  );
  return (
    <section className={sectionClass}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {collapsible ? (
            <button
              type="button"
              className="flex min-w-0 items-center gap-2 text-left"
              aria-expanded={!collapsed}
              onClick={toggleCollapsed}
            >
              <span aria-hidden="true">{collapsed ? "▸" : "▾"}</span>
              {title}
            </button>
          ) : (
            title
          )}
          {!collapsed && props.description ? (
            <p className="mt-1 break-words text-xs text-gray-600 dark:text-zinc-300">{props.description}</p>
          ) : null}
        </div>
        {props.rightSlot ? <div className="shrink-0">{props.rightSlot}</div> : null}
      </div>
      {!collapsed ? props.children : null}
    </section>
  );
}
